import snakeCase from 'lodash/snakeCase'
import { AnalyticsEventName } from './constants'

/**
 * Property naming: snake_case is the canonical style for event properties
 * (`price_anchor`, `results_count`, `merchant_key`, `click_id` …), and the
 * one lifecycle tools like Customer.io are mapped against. Older properties
 * were written in camelCase (`threadId`, `shopKey`, `categoryGroup`), often on
 * the same event as snake_case ones.
 *
 * Renaming them outright would silently break live experiment metrics,
 * Tinybird pipes and readout checks that filter on the camelCase keys, so the
 * lifecycle events below carry BOTH spellings. New code should read the
 * snake_case twin; the camelCase original can be dropped once nothing reads it.
 */
export const SNAKE_CASE_TWIN_EVENTS: ReadonlySet<string> = new Set<string>([
  AnalyticsEventName.PerformedDupe,
  AnalyticsEventName.SearchNoResults,
  AnalyticsEventName.DupeResultClicked,
  AnalyticsEventName.ViewedDupeResultPage,
  AnalyticsEventName.RegistrationCompleted,
  AnalyticsEventName.GuestAccountCreated,
])

const CAMEL_HUMP = /[a-z0-9][A-Z]/

export function toSnakeCase(key: string): string {
  return snakeCase(key)
}

/**
 * The snake_case twins missing from `properties`, for events in
 * `SNAKE_CASE_TWIN_EVENTS`. Additive only: never overwrites a key the caller
 * already set, and skips PostHog (`$…`) and internal (`_…`) keys.
 */
export function deriveSnakeCaseTwins(
  event: string,
  properties: Record<string, unknown> | undefined
): Record<string, unknown> | null {
  if (!properties || !SNAKE_CASE_TWIN_EVENTS.has(event)) return null

  const twins: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(properties)) {
    if (key.startsWith('$') || key.startsWith('_')) continue
    if (!CAMEL_HUMP.test(key)) continue
    const snake = toSnakeCase(key)
    if (snake in properties) continue
    twins[snake] = value
  }
  return Object.keys(twins).length > 0 ? twins : null
}
